
import { useState, useEffect, lazy, Suspense } from 'react';
import type { ReverseCalculationInput, ReverseCalculationResult } from './types/tax';
import { ReverseCalculator, SalaryComparison } from './components/calculator';
import { TaxYearSelector } from './components/education';
import { Header, Footer } from './components/layout';
import { ScenariosProvider } from './contexts/ScenariosContext';
import { performanceMonitor } from './utils/performance/webVitals';
import { PerformanceDashboard, PerformanceToggle } from './components/performance/PerformanceDashboard';
import { CalculatorSkeleton } from './components/ui/LoadingSkeleton';
import { initializeGA, initializeConsent } from './utils/analytics/googleAnalytics';
import { userTracking, trackCoreWebVitals } from './utils/analytics/userTracking';
import CookieConsent from './components/privacy/CookieConsent';
import { TaxDataManager } from './utils/taxYear/TaxDataManager';
import { ContactModal } from './components/contact';
import { B2bCta } from './components/contact';
import { DevErrorTrigger } from './components/dev/DevErrorTrigger';

// Lazy load heavy components
const TaxEducation = lazy(() => import('./components/education/TaxEducation'));

function App() {
  const [activeTab, setActiveTab] = useState<'calculator' | 'education'>('calculator');
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [taxYearDisplayName, setTaxYearDisplayName] = useState<string>('');
  
  // Calculator state lifted up to preserve between tab switches
  const [calculationResult, setCalculationResult] = useState<ReverseCalculationResult | null>(null);
  const [lastInput, setLastInput] = useState<ReverseCalculationInput | null>(null);

  useEffect(() => {
    const taxDataManager = TaxDataManager.getInstance();
    setTaxYearDisplayName(taxDataManager.getDisplayName());
    
    const handleTaxYearChange = () => {
      setTaxYearDisplayName(TaxDataManager.getInstance().getDisplayName());
    };
    
    taxDataManager.subscribe(handleTaxYearChange);
    
    // Initialize analytics systems
    initializeConsent();
    initializeGA();
    
    // Initialize performance monitoring with analytics integration
    performanceMonitor.subscribe((metrics) => {
      console.log('Performance metrics updated:', metrics);
      
      // Send Core Web Vitals to analytics
      trackCoreWebVitals(metrics);
    });

    // Track initial page view
    userTracking.trackPageView('SA Reverse Tax Calculator');
    
    return () => taxDataManager.unsubscribe(handleTaxYearChange);
  }, []);

  // Handle tab changes with tracking
  const handleTabChange = (newTab: 'calculator' | 'education') => {
    if (newTab !== activeTab) {
      userTracking.trackTabSwitch(activeTab, newTab);
      setActiveTab(newTab);
    }
  };

  return (
    <ScenariosProvider>
      <div className="min-h-screen bg-soft-gray dark:bg-slate-900" itemScope itemType="https://schema.org/WebApplication">
        <meta itemProp="name" content="SA Reverse Tax Calculator" />
        <meta itemProp="description" content="Calculate the gross salary needed for your desired net income in South Africa using current SARS tax brackets" />
        <meta itemProp="applicationCategory" content="FinanceApplication" />
        <meta itemProp="operatingSystem" content="Any" />
        <meta itemProp="isAccessibleForFree" content="true" />
        
        <Header 
          taxYearDisplayName={taxYearDisplayName}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" role="main" itemScope itemType="https://schema.org/Calculator">
          <meta itemProp="name" content="South African Reverse Tax Calculator" />
          <meta itemProp="description" content="Professional calculator for determining gross salary requirements from desired net income in South Africa" />
          
          <div className="space-y-8">
            {/* Tax Year Selector - Always visible */}
            <section aria-label="Tax year selection">
              <TaxYearSelector />
            </section>
            
            {/* Calculator Tab Content */}
            {activeTab === 'calculator' && (
              <section aria-label="Tax calculator" itemProp="mainEntity" itemScope itemType="https://schema.org/CalculatorAction">
                <h2 className="sr-only">Tax Calculator</h2>
                <ReverseCalculator 
                  calculationResult={calculationResult}
                  setCalculationResult={setCalculationResult}
                  lastInput={lastInput}
                  setLastInput={setLastInput}
                />
                <B2bCta />
                <aside aria-label="Salary comparison tool" className="mt-8">
                  <SalaryComparison />
                </aside>
              </section>
            )}
            
            {/* Education Tab Content */}
            {activeTab === 'education' && (
              <section aria-label="Tax education" itemScope itemType="https://schema.org/EducationalOrganization">
                <h2 className="sr-only">Tax Education</h2>
                <Suspense fallback={<CalculatorSkeleton />}>
                  <TaxEducation />
                </Suspense>
              </section>
            )}
          </div>
        </main>
        
        <Footer 
          onPrivacyClick={() => setShowPrivacyPolicy(true)} 
          onContactClick={() => setShowContactModal(true)} 
        />
        
        {/* Performance Monitoring Components (dev only) */}
        <PerformanceDashboard />
        <PerformanceToggle />
        
        {/* Cookie Consent Banner */}
        <CookieConsent />
        
        {/* Privacy Policy Modal */}
        {showPrivacyPolicy && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-charcoal dark:text-slate-100">Privacy Policy</h2>
                  <button
                    onClick={() => setShowPrivacyPolicy(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 text-2xl"
                    aria-label="Close privacy policy"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="prose dark:prose-invert max-w-none">
                  <section className="mb-6">
                    <h3 className="text-lg font-semibold mb-3 text-charcoal dark:text-slate-100">Data Collection</h3>
                    <p className="text-gray-600 dark:text-slate-300 mb-4">
                      The SA Reverse Tax Calculator is designed with privacy in mind. We collect minimal data to improve your experience:
                    </p>
                    <ul className="list-disc list-inside text-gray-600 dark:text-slate-300 space-y-1">
                      <li>Anonymous usage statistics (page views, feature usage)</li>
                      <li>Performance metrics (loading times, error rates)</li>
                      <li>Device information (browser type, screen size for responsive design)</li>
                    </ul>
                  </section>
                  
                  <section className="mb-6">
                    <h3 className="text-lg font-semibold mb-3 text-charcoal dark:text-slate-100">Optional Contact Information</h3>
                    <p className="text-gray-600 dark:text-slate-300">
                      On the optional export and consulting surfaces you may choose to provide your
                      email for lead follow-up. Providing your email is entirely optional — the
                      calculator itself never asks for or requires it, and submission is explicitly
                      opt-in. We do not share your email with third parties.
                    </p>
                  </section>
                  
                  <section className="mb-6">
                    <h3 className="text-lg font-semibold mb-3 text-charcoal dark:text-slate-100">What We Don't Collect</h3>
                    <ul className="list-disc list-inside text-gray-600 dark:text-slate-300 space-y-1">
                      <li>Personal financial information or income amounts</li>
                      <li>Location data or IP addresses</li>
                      <li>Credit card or banking information</li>
                      <li>Any contact details unless you explicitly submit them on an opt-in surface</li>
                    </ul>
                  </section>
                  
                  <section className="mb-6">
                    <h3 className="text-lg font-semibold mb-3 text-charcoal dark:text-slate-100">Cookies</h3>
                    <p className="text-gray-600 dark:text-slate-300">
                      We use cookies only for essential functionality and analytics (with your consent). 
                      You can manage your cookie preferences at any time using the Cookie Settings link in the footer.
                    </p>
                  </section>
                  
                  <section className="mb-6">
                    <h3 className="text-lg font-semibold mb-3 text-charcoal dark:text-slate-100">Third-Party Services</h3>
                    <p className="text-gray-600 dark:text-slate-300 mb-2">
                      With your consent, we may use:
                    </p>
                    <ul className="list-disc list-inside text-gray-600 dark:text-slate-300 space-y-1">
                      <li>Google Analytics for usage analytics</li>
                      <li>Sentry for error monitoring</li>
                    </ul>
                  </section>
                  
                  <section className="mb-6">
                    <h3 className="text-lg font-semibold mb-3 text-charcoal dark:text-slate-100">Disclaimer</h3>
                    <p className="text-gray-600 dark:text-slate-300">
                      This calculator is for educational purposes only. Always consult a qualified tax professional 
                      for official tax advice. We are not responsible for any financial decisions made based on 
                      the calculator's output.
                    </p>
                  </section>
                  
                  <section>
                    <h3 className="text-lg font-semibold mb-3 text-charcoal dark:text-slate-100">Contact</h3>
                    <p className="text-gray-600 dark:text-slate-300">
                      If you have questions about this privacy policy or our data practices, 
                      you can manage your preferences using the Cookie Settings.
                    </p>
                  </section>
                </div>
                
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-slate-700">
                  <button
                    onClick={() => setShowPrivacyPolicy(false)}
                    className="btn-primary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Contact Modal */}
        <ContactModal
          isOpen={showContactModal}
          onClose={() => setShowContactModal(false)}
        />

        {/* Dev-only Sentry test error trigger (tree-shaken in production builds) */}
        {import.meta.env.DEV && <DevErrorTrigger />}
      </div>
    </ScenariosProvider>
  );
}

export default App;
